import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Eye, EyeOff } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import { TEMPLATES_GUIDE_OPACITY } from "../../../i18n/keysTranslations";
import {
  BOX_SHADOW_FIELDS,
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import { BORDER_RADIUS, BORDER_RADIUS_BUTTON } from "../../../utils/constants";

const OPACITY_MIN = 0;
const OPACITY_MAX = 100;
const SLIDER_THUMB_SIZE = 20;
const INTEGER_DRAFT = /^\d{0,3}$/;

const clampPercent = (value) =>
  Math.min(OPACITY_MAX, Math.max(OPACITY_MIN, Math.round(value)));

const toPercent = (opacity) =>
  clampPercent(Number.isFinite(Number(opacity)) ? Number(opacity) * 100 : 0);

const sliderSx = {
  flex: 1,
  minWidth: 0,
  ml: 0,
  color: "#000",
  ".MuiSlider-track": {
    backgroundColor: "#000",
    height: 4,
  },
  ".MuiSlider-rail": {
    backgroundColor: "rgba(0,0,0,0.5)",
    height: 4,
  },
  ".MuiSlider-mark": {
    backgroundColor: "transparent",
  },
  ".MuiSlider-thumb": {
    width: SLIDER_THUMB_SIZE,
    height: SLIDER_THUMB_SIZE,
    border: "1px solid #e1e1e1",
    backgroundColor: "#fff",
  },
};

const numberInputSx = {
  width: 64,
  flexShrink: 0,
  height: 36,
  px: 1,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: "10px",
  bgcolor: "fields.background",
  boxShadow: BOX_SHADOW_FIELDS,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 500,
  color: "#000",
  textAlign: "center",
  appearance: "none",
  "&:disabled": {
    color: "text.disabled",
  },
};

const tooltipSlotSx = {
  p: 2,
  maxWidth: "none",
  width: 340,
  backgroundColor: "background.paper",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: BOX_SHADOW_THEME,
  borderRadius: BORDER_RADIUS,
  color: "text.primary",
  fontSize: 14,
  mt: "8px !important",
};

const GuideOpacityControl = ({
  opacity,
  onChange,
  onOpen,
  disabled = false,
}) => {
  const { t } = useTranslationApp();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(() => String(toPercent(opacity)));
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const percent = toPercent(opacity);
  const label = t(TEMPLATES_GUIDE_OPACITY);

  useEffect(() => {
    if (!isOpen) setDraft(String(toPercent(opacity)));
  }, [isOpen, opacity]);

  const commitPercent = (next) => {
    const clamped = clampPercent(next);
    setDraft(String(clamped));
    onChange(clamped / 100);
  };

  const closeTooltip = () => {
    if (!isOpen) return;
    setIsOpen(false);
    commitPercent(percent);
  };

  const toggleTooltip = () => {
    if (disabled) return;
    if (isOpen) {
      closeTooltip();
      return;
    }
    setIsOpen(true);
    onOpenRef.current?.();
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (event.target.closest?.("[data-guide-opacity-tooltip]")) return;
        closeTooltip();
      }}
    >
      <Box sx={{ display: "inline-flex" }}>
        <Tooltip
          open={isOpen}
          arrow
          placement="bottom"
          disableFocusListener
          disableHoverListener
          disableTouchListener
          disableInteractive={false}
          title={
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 1.5,
                }}
              >
                {label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Slider
                  min={OPACITY_MIN}
                  max={OPACITY_MAX}
                  step={1}
                  value={percent}
                  disabled={disabled}
                  aria-label={label}
                  onChange={(_event, value) => {
                    const next = Array.isArray(value) ? value[0] : value;
                    commitPercent(typeof next === "number" ? next : percent);
                  }}
                  sx={sliderSx}
                />
                <Box
                  component="input"
                  type="text"
                  inputMode="numeric"
                  disabled={disabled}
                  value={draft}
                  aria-label={label}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (!INTEGER_DRAFT.test(raw)) return;
                    setDraft(raw);
                    if (raw === "") return;
                    const parsed = Number(raw);
                    if (!Number.isFinite(parsed)) return;
                    onChange(clampPercent(parsed) / 100);
                  }}
                  onBlur={() => {
                    if (draft === "") {
                      commitPercent(percent);
                      return;
                    }
                    const parsed = Number(draft);
                    commitPercent(Number.isFinite(parsed) ? parsed : percent);
                  }}
                  sx={numberInputSx}
                />
              </Box>
            </Box>
          }
          componentsProps={{
            tooltip: {
              "data-guide-opacity-tooltip": "true",
              sx: tooltipSlotSx,
            },
            arrow: {
              sx: { color: "background.paper" },
            },
          }}
        >
          <span>
            <IconButton
              size="small"
              aria-label={label}
              aria-expanded={isOpen}
              aria-haspopup="true"
              disabled={disabled}
              onClick={toggleTooltip}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #E4E4E7",
                borderRadius: BORDER_RADIUS_BUTTON,
                bgcolor: isOpen ? "#F4F4F5" : "#fff",
                color: "text.primary",
              }}
            >
              {percent > 0 ? <Eye size={16} /> : <EyeOff size={16} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default GuideOpacityControl;
